/**
 *  @class
 *  @function FacetsToggle
 */
class FacetToggle extends HTMLElement {
  constructor() {
    super();
    this.init();
  }
  init() {
    this.drawer = document.getElementById("Facet-Drawer");
    this.container = this.closest(".facets--bar-sticky");

    // Add functionality to buttons
    this.addEventListener("click", (e) => {
      e.preventDefault();
      if (OverlayManager.isOpen(this.drawer)) {
        OverlayManager.close(this.drawer);
      } else {
        OverlayManager.open(this.drawer);
      }
    });
    if (this.container) {
      this.enableSticky();
    }
  }
  enableSticky() {
    let rootMargin =
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--header-height",
        ),
        10,
      ) + 1;
    this.observer = new IntersectionObserver(
      ([e]) => {
        e.target.classList.toggle(
          "is-pinned",
          e.intersectionRatio <= 1 && e.intersectionRatio > 0,
        );
      },
      {
        threshold: [1],
        rootMargin: `-${rootMargin}px 0px 0px 0px`,
      },
    );

    this.observer.observe(this.container);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}
customElements.define("facet-toggle", FacetToggle);

/**
 *  @class
 *  @function FacetFiltersForm
 */
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      if (event.target.id === "compare_toggle") {
        return;
      }
      this.onSubmitHandler(event);
    }, 500);

    this.querySelector("form").addEventListener(
      "input",
      this.debouncedOnSubmit.bind(this),
    );
  }

  static setListeners() {
    FacetFiltersForm.onHistoryChangeBound = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", FacetFiltersForm.onHistoryChangeBound);
  }

  static removeListeners() {
    if (FacetFiltersForm.onHistoryChangeBound) {
      window.removeEventListener(
        "popstate",
        FacetFiltersForm.onHistoryChangeBound,
      );
    }
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const container = document.getElementsByClassName("thb-filter-count");
    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");

    for (var item of container) {
      item.classList.add("loading");
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderWithViewTransition(update) {
    // Animate the grid swap with the View Transitions API when available.
    // Skipped while an overlay is open (mobile facets drawer would be
    // painted over by the transition's top layer) and when animations are
    // disabled, which includes visitors preferring reduced motion.
    const canTransition =
      typeof document.startViewTransition === "function" &&
      document.body.classList.contains("animations-true") &&
      !document.body.classList.contains("open-cc");

    if (canTransition) {
      document.startViewTransition(update);
    } else {
      update();
    }
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          {
            html,
            url,
          },
        ];
        FacetFiltersForm.renderWithViewTransition(() => {
          FacetFiltersForm.renderFilters(html, event);
          FacetFiltersForm.renderProductGridContainer(html);
          FacetFiltersForm.renderProductCount(html);
          FacetFiltersForm.restoreFilterFocus();
        });
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderWithViewTransition(() => {
      FacetFiltersForm.renderFilters(html, event);
      FacetFiltersForm.renderProductGridContainer(html);
      FacetFiltersForm.renderProductCount(html);
      FacetFiltersForm.restoreFilterFocus();
    });
  }

  static renderProductGridContainer(html) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    document.getElementById("ProductGridContainer").innerHTML =
      parsedHTML.getElementById("ProductGridContainer").innerHTML;
    // #ProductCount carries the paginated total; it is absent when nothing matched.
    const countElement = parsedHTML.getElementById("ProductCount");
    document.dispatchEvent(
      new CustomEvent("facets:updated", {
        detail: {
          searchParams: FacetFiltersForm.searchParamsPrev,
          productsCount:
            parseInt(countElement && countElement.dataset.count, 10) || 0,
        },
      }),
    );
  }

  static renderProductCount(html) {
    const countHtml = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductCount");
    const container = document.getElementsByClassName("thb-filter-count");

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove("loading");
      }
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const interacted = event ? event.target : null;
    const interactedForm =
      interacted && interacted.form ? interacted.form : null;
    const interactedRow = interacted
      ? interacted.closest("collapsible-row")
      : null;
    // With "Show selected values first" enabled, the group being interacted
    // with is re-rendered too so the checked value moves to the top right
    // away. Price range inputs keep the skip behaviour — re-rendering there
    // would drop focus mid-typing.
    const reRenderInteracted = !!(
      interacted &&
      interacted.type === "checkbox" &&
      interactedForm &&
      interactedForm.dataset.selectedFirst === "true"
    );

    FacetFiltersForm.pendingFocus = null;
    if (reRenderInteracted && interactedForm.id && interacted.name) {
      FacetFiltersForm.pendingFocus = {
        formId: interactedForm.id,
        name: interacted.name,
        value: interacted.value,
      };
    }

    // Desktop and mobile rows share the same data-index values, so
    // replacements are scoped per form to keep each form's own markup.
    ["FacetFiltersForm", "FacetFiltersFormMobile"].forEach((formId) => {
      const targetForm = document.getElementById(formId);
      const sourceForm = parsedHTML.getElementById(formId);
      if (!targetForm || !sourceForm) return;

      sourceForm.querySelectorAll("collapsible-row").forEach((element) => {
        const targetRow = targetForm.querySelector(
          `collapsible-row[data-index="${element.dataset.index}"]`,
        );
        if (!targetRow) return;

        const isInteractedRow = interactedRow && targetRow.contains(interacted);

        if (isInteractedRow && !reRenderInteracted) {
          FacetFiltersForm.renderCounts(element, targetRow);
        } else {
          targetRow.innerHTML = element.innerHTML;
        }
      });
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
  }

  static restoreFilterFocus() {
    const pending = FacetFiltersForm.pendingFocus;
    FacetFiltersForm.pendingFocus = null;
    if (!pending) return;
    const form = document.getElementById(pending.formId);
    if (!form) return;
    const input = form.querySelector(
      `input[name="${CSS.escape(pending.name)}"][value="${CSS.escape(pending.value)}"]`,
    );
    if (input) {
      input.focus({ preventScroll: true });
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [".mobile-filter-count"];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML;
    });
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector(".facets__selected");
    const sourceElement = source.querySelector(".facets__selected");

    if (sourceElement && targetElement) {
      target.querySelector(".facets__selected").outerHTML =
        source.querySelector(".facets__selected").outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      {
        searchParams,
      },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest("form"));
    const searchParams = new URLSearchParams(formData);
    if (searchParams.get("filter.v.price.gte") === "0.00") {
      searchParams.delete("filter.v.price.gte");
    }
    if (document.querySelector(".price_slider")) {
      if (
        searchParams.get("filter.v.price.lte") ===
        parseFloat(document.querySelector(".price_slider").dataset.max).toFixed(
          2,
        )
      ) {
        searchParams.delete("filter.v.price.lte");
      }
    }
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1,
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

/**
 *  @class
 *  @function Facetremove
 */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("a").forEach((item) => {
      item.addEventListener("click", (event) => {
        event.preventDefault();
        const form =
          this.closest("facet-filters-form") ||
          document.querySelector("facet-filters-form");
        form.onActiveFilterClick(event);
      });
    });
  }
}

customElements.define("facet-remove", FacetRemove);

/**
 *  @class
 *  @function FacetToolbar
 */
class FacetToolbar extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.compareToggle = this.querySelector("#compare_toggle");
    if (this.compareToggle) {
      this.compareToggle.addEventListener("change", this.onCompare);
      this.compareToggle.checked =
        document.body.classList.contains("compare-true");
    }
  }
  onCompare(e) {
    document.body.classList.toggle("compare-true");
    e.preventDefault();
    return false;
  }
}
customElements.define("facet-toolbar", FacetToolbar);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    let slider = this.querySelector(".price_slider"),
      amounts = this.querySelector(".price_slider_amount"),
      args = {
        start: [
          parseFloat(slider.dataset.minValue || 0),
          parseFloat(slider.dataset.maxValue || slider.dataset.max),
        ],
        connect: true,
        step: 10,
        direction: document.dir,
        handleAttributes: [
          { "aria-label": "lower" },
          { "aria-label": "upper" },
        ],
        range: {
          min: 0,
          max: parseFloat(slider.dataset.max),
        },
      },
      event = new CustomEvent("input"),
      form =
        this.closest("facet-filters-form") ||
        document.querySelector("facet-filters-form");

    if (slider.classList.contains("noUi-target")) {
      slider.noUiSlider.destroy();
    }
    noUiSlider.create(slider, args);
    this.slider = slider;

    slider.noUiSlider.on("update", function (values) {
      amounts.querySelector(".field__input_min").value = values[0];
      amounts.querySelector(".field__input_max").value = values[1];
    });
    slider.noUiSlider.on("change", function (values) {
      form.querySelector("form").dispatchEvent(event);
    });
  }

  disconnectedCallback() {
    if (this.slider && this.slider.noUiSlider) {
      this.slider.noUiSlider.destroy();
    }
  }
}
customElements.define("price-slider", PriceSlider);
